import React, { useRef } from 'react';
import './About.css';
import { Link } from 'react-router-dom';
import { trackCTAClick } from '../utils/analytics';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { FaArrowRight, FaCog, FaLightbulb, FaRocket } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const reduceMotion = useReducedMotion();

  return (
    <div className="about-redesign">
      <Helmet>
        <title>About Media Mars - UK Technology Consulting & SpocoDoc Creator | Est. 2014</title>
        <meta name="description" content="Media Mars Limited is a UK technology consulting firm founded in 2014. We combine strategy, automation, data, integration, custom development and SpocoDoc delivery." />
        <meta name="keywords" content="Media Mars Limited, UK technology consulting, SpocoDoc creator, IT strategy, automation, data analytics, system integration, custom development, Microsoft technology expertise" />
        <meta property="og:title" content="About Media Mars - UK Technology Consulting & SpocoDoc Creator" />
        <meta property="og:description" content="Founded in 2014, Media Mars combines technology strategy with hands-on delivery across automation, data, integration, custom development and SpocoDoc." />
        <meta property="og:url" content="https://www.media-mars.com/about" />
        <meta property="og:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Media Mars - UK Technology Consulting" />
        <meta name="twitter:description" content="Founded in 2014, Media Mars combines technology strategy with hands-on delivery." />
        <meta name="twitter:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <link rel="canonical" href="https://www.media-mars.com/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Media Mars Limited",
            "url": "https://www.media-mars.com",
            "logo": "https://www.media-mars.com/MediaMarsLogo.png",
            "foundingDate": "2014",
            "areaServed": "GB",
            "description": "UK technology consulting firm providing strategy, automation, data analytics, system integration, custom development and the SpocoDoc SaaS platform.",
            "address": { "@type": "PostalAddress", "addressCountry": "GB" },
            "knowsAbout": ["IT Strategy", "Business Process Automation", "Data Analytics", "Power BI", "Azure", "Power Platform", "System Integration", "Custom Software"]
          })}
        </script>
      </Helmet>

      <section className="about-hero">
        <div className="about-hero__inner">
          <motion.div
            className="about-hero__copy"
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.45, ease: 'easeOut' }}
          >
            <span className="about-hero__tag">About Media Mars</span>
            <h1>Technology should move your business forward, not hold it back.</h1>
            <p>Since 2014, Media Mars has combined technology strategy with hands-on delivery - helping organisations simplify systems, automate processes, improve visibility and build solutions around the way they actually work.</p>
          </motion.div>
          <motion.div
            className="about-hero__visual"
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : 0.08, ease: 'easeOut' }}
          >
            <img src="/Images/About/about-hero-visual.png" alt="Media Mars technology strategy and delivery visual" loading="eager" decoding="async" fetchPriority="high" />
          </motion.div>
        </div>
      </section>

      <section className="about-credibility" aria-label="Media Mars credibility">
        <div className="about-credibility__inner">
          <FadeIn className="credibility-item">
            <strong>Since 2014</strong>
            <span>UK technology consultancy</span>
          </FadeIn>
          <FadeIn className="credibility-item" delay={0.04}>
            <strong>Strategy + Delivery</strong>
            <span>From roadmap to implementation</span>
          </FadeIn>
          <FadeIn className="credibility-item" delay={0.08}>
            <strong>Microsoft &amp; Cloud</strong>
            <span>Azure, Power Platform, Microsoft 365, data and integration</span>
          </FadeIn>
          <FadeIn className="credibility-item" delay={0.12}>
            <strong>We Build Products</strong>
            <span>Creator of SpocoDoc</span>
          </FadeIn>
        </div>
      </section>

      <section className="about-story">
        <div className="about-story__inner">
          <FadeIn className="section-heading">
            <span className="section-tag">Our Story</span>
            <h2>Built around practical technology delivery.</h2>
          </FadeIn>
          <div className="about-timeline">
            <FadeIn className="timeline-item" delay={0.04}>
              <span>2014</span>
              <h3>Media Mars founded</h3>
              <p>Started as a UK technology services business focused on helping organisations modernise and operate more effectively.</p>
            </FadeIn>
            <FadeIn className="timeline-item" delay={0.08}>
              <span>Evolving with our clients</span>
              <h3>From IT services to transformation</h3>
              <p>Expanded into strategy, cloud, automation, systems integration, data and business applications as client needs became more complex.</p>
            </FadeIn>
            <FadeIn className="timeline-item" delay={0.12}>
              <span>Building, not just advising</span>
              <h3>SpocoDoc</h3>
              <p>Developed our own SaaS platform for budgets, invoices, expenses and approval workflows.</p>
            </FadeIn>
            <FadeIn className="timeline-item" delay={0.16}>
              <span>Today</span>
              <h3>Strategy backed by delivery</h3>
              <p>Media Mars combines senior technology leadership with hands-on implementation across Microsoft cloud, automation, data, integration and custom development.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="about-principles">
        <div className="about-principles__inner">
          <FadeIn className="section-heading">
            <span className="section-tag">What Sets Us Apart</span>
            <h2>Senior thinking, practical execution.</h2>
          </FadeIn>
          <div className="principles-list">
            <FadeIn className="principle-item" delay={0.04}>
              <span>01</span>
              <div>
                <h3>Strategy and delivery in one team</h3>
                <p>We can define the direction and then actually build what is required.</p>
              </div>
            </FadeIn>
            <FadeIn className="principle-item" delay={0.08}>
              <span>02</span>
              <div>
                <h3>Problem first, technology second</h3>
                <p>We start with the business outcome, not a product catalogue.</p>
              </div>
            </FadeIn>
            <FadeIn className="principle-item" delay={0.12}>
              <span>03</span>
              <div>
                <h3>Built from real delivery experience</h3>
                <p>SpocoDoc and our custom solutions reflect the same problems we help clients solve.</p>
              </div>
            </FadeIn>
            <FadeIn className="principle-item" delay={0.16}>
              <span>04</span>
              <div>
                <h3>Flexible senior-level expertise</h3>
                <p>From focused advisory work to embedded technology leadership and implementation.</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="about-belief">
        <div className="about-belief__inner">
          <FadeIn className="about-belief__lead">
            <span className="section-tag light">What We Believe</span>
            <h2>Technology earns its place when it makes the business simpler, faster, more informed or more capable.</h2>
          </FadeIn>
          <div className="belief-grid">
            <FadeIn className="belief-item" delay={0.04}>
              <FaLightbulb />
              <h3>Clarity over complexity</h3>
              <p>Technology should make work easier to understand and operate.</p>
            </FadeIn>
            <FadeIn className="belief-item" delay={0.08}>
              <FaCog />
              <h3>Outcomes over products</h3>
              <p>The right solution is the one that solves the problem, not the one we happen to sell.</p>
            </FadeIn>
            <FadeIn className="belief-item" delay={0.12}>
              <FaRocket />
              <h3>Delivery over theory</h3>
              <p>Good strategy matters only when it can be implemented.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <FadeIn>
          <h2>Have a technology challenge? Let's talk.</h2>
          <p>Tell us what is getting in the way and we'll help you work out the most practical next step.</p>
          <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Get in Touch', 'about-cta')}>Get in Touch <FaArrowRight /></Link>
        </FadeIn>
      </section>
    </div>
  );
};

export default About;
